import React from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../../components/layout/AppLayout';
import Header from '../../../../../shared/components/layout/Header';
import { dummyData } from './data';
import ProgressOverview from './ProgressOverview';
import StageSnapshot from './StageSnapshot';
import TodaysWork from './TodaysWork';
import DelayedTasks from './DelayedTasks';
import WorkStopped from './WorkStopped';
import Accountability from './Accountability';
import DashboardHeader from './DashboardHeader';
import DelaysAndAlerts from './DelaysAndAlerts';
import MaterialStatus from './MaterialStatus';
import QuickSummary from './QuickSummary';
import PhotoProof from './PhotoProof';
import { useProjectsList } from '../../../../../../contexts/ProjectsContext';
import moment from 'moment';
import { getOperationsDailyStats } from '../../../../services/api/implementation/impl';
import Loader from '../../../../../shared/components/ui/Loader';
import toast from 'react-hot-toast';
import ErrorScreen from '../../../../../shared/components/ui/ErrorScreen';

const getErrorMessage = (error) => {
    if (!error) return 'Unable to load daily report.';
    const msg = error?.response?.data?.message || error?.message;
    if (msg && typeof msg === 'string') {
        // Show a clear message when report run date is too early for Today's report
        const reportRunDateMatch = msg.match(/reportRunDate must be on or after (\d{4}-\d{2}-\d{2}) for TODAY reports/i);
        if (reportRunDateMatch) {
            const minDate = reportRunDateMatch[1];
            return `Today's report is only available from ${minDate} onwards. Please select a date on or after ${minDate} to view the report.`;
        }
        return msg;
    }
    const status = error?.response?.status;
    if (status === 404) return 'Daily report not found for this date.';
    if (status === 500) return 'Server error. Please try again later.';
    if (status >= 502 && status <= 504) return 'Service temporarily unavailable. Please try again.';
    return 'Unable to load daily report. Please try again.';
};

const DailyReports = () => {
    const navigate = useNavigate();
    const { projectsForDropdown, projectsError, refetchProjects } = useProjectsList();
    const projects = projectsForDropdown?.length > 0 ? projectsForDropdown : [{ label: "No Project", id: null, value: null }];
    const [currentProject, setCurrentProject] = React.useState(projects[0]?.label || "No Project");
    // Default to yesterday so only past dates are selectable (today restricted)
    const [selectedDate, setSelectedDate] = React.useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        return d;
    });

    React.useEffect(() => {
        if (projects?.length > 0 && projects[0]?.id !== null && currentProject === "No Project") {
            setCurrentProject(projects[0]?.label);
        }
    }, [projects, currentProject]);

    const [dailyData, setDailyData] = React.useState(null);
    const [loadingStats, setLoadingStats] = React.useState(false);
    const [reportError, setReportError] = React.useState(null);

    // Check for project API error
    React.useEffect(() => {
        if (projectsError) {
            const message = projectsError?.response?.data?.message || projectsError?.message || 'Unable to load projects.';
            setReportError(message);
            toast.error(message);
        }
    }, [projectsError]);

    // Retry handler - retries both project API and report API
    const handleRetry = React.useCallback(async () => {
        setReportError(null);
        setLoadingStats(true);

        try {
            // First, retry fetching projects
            await refetchProjects();

            // Then retry fetching report stats
            const projectObj = projectsForDropdown?.find(p => p.label === currentProject);
            const projectId = projectObj?.id;

            if (projectId) {
                // API expects next day: selected 14 Feb → 15 Feb; selected 31 Dec 2025 → 1 Jan 2026
                const apiDate = moment(selectedDate).add(1, 'days');
                const dateStr = apiDate.format('YYYY-MM-DD');
                const data = await getOperationsDailyStats(projectId, dateStr);
                setDailyData(data);
            }
        } catch (error) {
            console.error('Retry failed', error);
            const message = getErrorMessage(error);
            setReportError(message);
            toast.error(message);
        } finally {
            setLoadingStats(false);
        }
    }, [refetchProjects, projectsForDropdown, currentProject, selectedDate]);



    const fetchStats = React.useCallback(async () => {
        const projectObj = projectsForDropdown?.find(p => p.label === currentProject);
        const projectId = projectObj?.id;
        if (!projectId) return;

        setReportError(null);
        setLoadingStats(true);
        try {
            // API expects next day: selected 14 Feb → 15 Feb; selected 31 Dec 2025 → 1 Jan 2026
            const apiDate = moment(selectedDate).add(1, 'days');
            const dateStr = apiDate.format('YYYY-MM-DD');
            const data = await getOperationsDailyStats(projectId, dateStr);
            setDailyData(data);
        } catch (error) {
            console.error('Failed to fetch daily stats', error);
            const message = getErrorMessage(error);
            setReportError(message);
            setDailyData(null);
            toast.error(message);
        } finally {
            setLoadingStats(false);
        }
    }, [currentProject, selectedDate, projectsForDropdown]);

    React.useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    // Prepare data for StageSnapshot
    const stageSnapshotStages = dailyData?.stageSnapshotPercentBlock?.stageSnapshotPercents?.map((s, i) => ({
        name: s.stage,
        percentage: Math.round(Number(s.percent) || 0),
        color: dummyData.stageColors?.[i % dummyData.stageColors.length] || '#1F4B55'
    })) || [];
    // const stageSnapshotStages = dummyData.stages;

    // Prepare data for Accountability
    const accountabilityPeople = dailyData?.accountabilityBlock?.assignments || [];
    // const accountabilityPeople = dummyData.accountability;

    // Prepare data for PhotoProof
    const photoProofPhotos = dailyData?.photoProofBlock?.dailyTasks?.flatMap(task =>
        (task.images || []).map(img => ({
            ...img,
            taskName: task.liveTask?.name || task.name,
            time: task.date
        }))
    ) || [];
    // const photoProofPhotos = dummyData.photoProof;

    return (
        <Layout>
            <Loader isVisible={loadingStats} />
            <div className="bg-[#F8FAFC] min-h-screen">
                {/* <Header backArrow={false} data={{ name: 'Daily Reports' }} /> */}
                <div className="sticky top-0 z-40 bg-[#EEF3F7] backdrop-blur pt-6 px-6">
                    <DashboardHeader
                        projectName={currentProject}
                        onProjectChange={setCurrentProject}
                        lastUpdated={dailyData?.lastUpdated || ''}
                        reportDate={moment(selectedDate).format('D MMM YYYY')}
                        date={selectedDate}
                        onDateChange={setSelectedDate}
                        projects={projects}
                        onViewSample={() => navigate('/builder/operations/daily-reports/sample')}
                    />
                </div>
                <div className="px-6 pb-6 max-w-[1600px] mx-auto">
                    {reportError ? (
                        <ErrorScreen
                            title="Unable to Load Daily Report"
                            message={reportError}
                            onRetry={() => window.location.reload()}
                            isRetrying={loadingStats}
                        />
                    ) : (
                        <div className="flex flex-col gap-6">
                            {/* Main Content Grid - Split into Left (2/3) and Right (1/3) blocks for independent vertical flow */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                                {/* Left Block - Spans 2 Columns */}
                                <div className="lg:col-span-2 flex flex-col gap-6">

                                    {/* ProgressOverview */}
                                    <div className="lg:col-span-2 h-full">
                                        <ProgressOverview
                                            today={dailyData?.dailyPercentBlock || { percentage: 0, trend: 0, description: '' }}
                                            overall={dailyData?.overallPercentBlock || { percentage: 0, status: 'Pending' }}
                                        />
                                    </div>
                                    {/* Row 1 of Left Block */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <TodaysWork work={dailyData?.todaysWorkBlock || { inProgress: [], completed: [] }} />
                                        </div>
                                        <div className="flex flex-col gap-6">
                                            <DelayedTasks tasks={dailyData?.todaysWorkBlock?.delayedAtStart || []} />
                                            <WorkStopped tasks={dailyData?.todaysWorkBlock?.delayedDuringTask || []} />
                                        </div>
                                    </div>

                                    {/* Stacked Items in Left Block */}
                                    <div>
                                        <DelaysAndAlerts alerts={dailyData?.delayedAndAlertsBlock?.tasks || []} />
                                    </div>
                                    <div>
                                        <QuickSummary
                                            summary={{
                                                text: dummyData.quickSummary.text,
                                                tomorrowsPlanBlock: dailyData?.tomorrowsPlanBlock
                                            }}
                                        />
                                    </div>
                                </div>
                                {/* Right Block - Spans 1 Column - Independent Stack */}
                                <div className="flex flex-col gap-6 lg:mt-0">
                                    <div className="lg:col-span-1 h-full">
                                        <StageSnapshot stages={stageSnapshotStages} />
                                    </div>
                                    <div>
                                        <Accountability people={accountabilityPeople} />
                                    </div>
                                    <div>
                                        <MaterialStatus materials={dailyData?.dailyUsedMaterialBlock?.materialStats ?? []} />
                                    </div>
                                    <div>
                                        <PhotoProof photos={photoProofPhotos} />
                                    </div>
                                </div>

                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default DailyReports;

